import React from 'react';
import { Sparkles } from 'lucide-react';

/**
 * God Mode: "What I understood" chip strip.
 * Shows the recognized fine-grained intent + any extracted entities
 * (relationship, discipline level, days requested, etc.) above the
 * clarification card so stewards can quickly correct any misread.
 *
 * Renders NOTHING when the God Mode block is absent or empty.
 */
const HUMAN_LABELS = {
  relationship: 'Relationship',
  discipline_level: 'Discipline level',
  days_requested: 'Days requested',
  time_reference: 'Timing',
  meeting_pending: 'Meeting pending',
  had_representation: 'Steward present',
  reported_safety: 'Reported to supervisor',
  emotional_signal: 'Emotional tone',
};

const INTENT_LABELS = {
  bereavement_leave: 'Bereavement leave',
  overtime_refusal: 'Overtime refusal',
  overtime_bypass: 'Overtime bypass',
  mandatory_overtime: 'Mandatory overtime',
  discipline: 'Discipline',
  discharge: 'Discharge / termination',
  grievance_filing: 'Grievance filing',
  sick_leave: 'Sick leave',
  fmla: 'FMLA',
  vacation: 'Vacation / time off',
  safety_hazard: 'Safety hazard',
  refuse_unsafe_work: 'Right to refuse unsafe work',
  callout: 'Callout / ARCOS',
  seniority: 'Seniority',
  layoff: 'Layoff',
  recall: 'Recall',
  shift_trade: 'Shift trade',
  weingarten: 'Weingarten rights',
};

const formatValue = (key, val) => {
  if (val === true) return 'yes';
  if (val === false) return 'no';
  if (key === 'days_requested') return `${val} days`;
  return String(val).replace(/-/g, ' ');
};

export const GodModeUnderstoodPanel = ({ godMode }) => {
  if (!godMode) return null;
  const intent = godMode.intent;
  const entities = godMode.entities || {};
  const hasIntent = intent && intent !== 'general_guidance';
  const entityEntries = Object.entries(entities).filter(([k]) => k in HUMAN_LABELS);
  if (!hasIntent && entityEntries.length === 0) return null;

  return (
    <div
      className="gm-understood"
      data-testid="god-mode-understood-panel"
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 8,
        padding: '10px 14px',
        marginBottom: 12,
        borderRadius: 10,
        background: 'linear-gradient(90deg, #fef3c7 0%, #fef9c3 100%)',
        border: '1px solid #fde68a',
        color: '#78350f',
        fontSize: 13,
      }}
    >
      <Sparkles size={16} style={{ color: '#b45309', flexShrink: 0 }} />
      <span style={{ fontWeight: 600 }}>Stewie understood:</span>
      {hasIntent && (
        <span
          data-testid="gm-intent-chip"
          style={{
            padding: '2px 10px',
            borderRadius: 999,
            background: '#fbbf24',
            color: '#78350f',
            fontWeight: 600,
          }}
        >
          {INTENT_LABELS[intent] || intent}
        </span>
      )}
      {entityEntries.map(([k, v]) => (
        <span
          key={k}
          data-testid={`gm-entity-${k}`}
          style={{
            padding: '2px 10px',
            borderRadius: 999,
            background: '#fff7ed',
            border: '1px solid #fed7aa',
            color: '#7c2d12',
          }}
        >
          <strong>{HUMAN_LABELS[k]}:</strong> {formatValue(k, v)}
        </span>
      ))}
    </div>
  );
};

/**
 * Renders the God Mode clarifying questions as clickable pills.
 * Falls back to a single line if the caller only has `clarifying_question`.
 */
export const GodModeClarifyPills = ({ questions, onPick }) => {
  if (!questions || questions.length === 0) return null;
  return (
    <div
      className="gm-clarify-pills"
      data-testid="god-mode-clarify-pills"
      style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}
    >
      {questions.map((q, idx) => (
        <button
          key={idx}
          type="button"
          data-testid={`god-mode-clarify-pill-${idx}`}
          onClick={() => onPick && onPick(q)}
          style={{
            textAlign: 'left',
            padding: '10px 14px',
            borderRadius: 10,
            border: '1px solid #d1d5db',
            background: '#ffffff',
            color: '#111827',
            cursor: onPick ? 'pointer' : 'default',
            fontSize: 14,
            lineHeight: 1.4,
            transition: 'background 0.15s ease',
          }}
          onMouseOver={(e) => (e.currentTarget.style.background = '#f9fafb')}
          onMouseOut={(e) => (e.currentTarget.style.background = '#ffffff')}
        >
          {q}
        </button>
      ))}
    </div>
  );
};
